import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { config } from '../../../config/config';
import { getClient } from '../../main/graphql';
import type { RootState } from '../../main/store/root.store';
import type {
    IGetGamesBySlugsQuery,
    IGetGamesBySlugsQueryVariables,
    IGetLaunchUrLsQuery,
    IGetLaunchUrLsQueryVariables,
} from '../queries/gameplay.graphql';
import { GetGamesBySlugsDocument, GetLaunchUrLsDocument } from '../queries/gameplay.graphql';

interface ISlot {
    isSearchOpen: boolean;
    slug?: string;
    internalGameId?: string;
    isLoadingLaunchUrl: boolean;
    isLoadingGameData: boolean;
    launchUrl?: string;
    hasError: boolean;
    currency: string;
    errorCode?: string;
}

interface IGameplayState {
    slots: Record<string | number, ISlot>;
    slotCount: number;
    games: Record<
        string,
        Exclude<IGetGamesBySlugsQuery['getGamesBySlugs'], undefined | null>[number]
    >;
    refCount: number;
}

export const getGameInformation = createAsyncThunk(
    'gameplay/getGameInformation',
    async ({
        locale,
        skinId,
        gameSlugs,
    }: {
        locale: string;
        skinId: string;
        gameSlugs: string[];
    }) => {
        const result = await getClient()
            .query<IGetGamesBySlugsQuery, IGetGamesBySlugsQueryVariables>(GetGamesBySlugsDocument, {
                locale,
                skinId,
                slugs: gameSlugs,
                xTenantId: config.TENANT_ID,
            })
            .toPromise();

        if (result.error) {
            throw result.error;
        }

        return result.data;
    },
);

export const getLaunchUrls = createAsyncThunk(
    'gameplay/getLaunchUrls',
    async ({
        games,
        isMobile,
        isDemoPlay,
        currencyCode,
        language,
        userProfileId,
        origin,
        countryCode,
    }: {
        games: Array<{ internalGameId: string }>;
        isMobile: boolean;
        isDemoPlay: boolean;
        currencyCode: string;
        language: string;
        userProfileId: string;
        origin: string;
        countryCode: string;
    }) => {
        const result = await getClient()
            .query<IGetLaunchUrLsQuery, IGetLaunchUrLsQueryVariables>(
                GetLaunchUrLsDocument,
                {
                    details: {
                        isMobile,
                        isDemoPlay,
                        currencyCode,
                        language,
                        userProfileId,
                        origin,
                        countryCode,
                    },
                    games,
                    skinId: config.SKIN_ID,
                    xTenantId: config.TENANT_ID,
                },
                {
                    requestPolicy: 'network-only',
                },
            )
            .toPromise();

        if (result.error || !result.data) {
            throw result.error;
        }

        return { games: result.data.getLaunchURLs, currency: currencyCode };
    },
);

function getSlotBy(slots: Record<string | number, ISlot>, key: 'internalGameId' | 'slug') {
    return Object.keys(slots).reduce((acc, item) => {
        const slot = slots[item];
        const index = slot[key];

        if (!index) {
            return acc;
        }

        acc[index] = slot;
        return acc;
    }, {} as Record<string, ISlot>);
}

const INITIAL_STATE = {
    slots: {
        0: {
            isSearchOpen: false,
            isLoadingLaunchUrl: false,
            isLoadingGameData: false,
            hasError: false,
            currency: '',
        },
    },
    slotCount: 1,
    games: {},
    refCount: 0,
} as IGameplayState;

export const gameplayStore = createSlice({
    name: 'gameplay',
    initialState: INITIAL_STATE,
    reducers: {
        incrementRefCount(state) {
            state.refCount++;
        },
        decrementRefCount(state) {
            state.refCount--;

            if (state.refCount === 0) {
                state.games = INITIAL_STATE.games;
                state.refCount = 0;
                state.slotCount = INITIAL_STATE.slotCount;
                state.slots = INITIAL_STATE.slots;
            }
        },
        setSlotCount(state, { payload }: PayloadAction<number>) {
            state.slots = Array.from({ length: payload })
                .fill(null)
                .map((_, i) => {
                    if (state.slots[i]) {
                        return state.slots[i];
                    }

                    return {
                        isSearchOpen: true,
                        isLoadingLaunchUrl: false,
                        isLoadingGameData: false,
                        hasError: false,
                        currency: '',
                    };
                })
                .reduce((acc, item, index) => {
                    acc[index] = item;
                    return acc;
                }, {} as Record<number | string, ISlot>);

            state.slotCount = payload;
        },
        toggleSearch(state, { payload }: PayloadAction<{ slot: number }>) {
            state.slots[payload.slot].isSearchOpen = !state.slots[payload.slot].isSearchOpen;
        },
        closeSearch(state, { payload }: PayloadAction<{ slot: number }>) {
            state.slots[payload.slot].isSearchOpen = false;
        },
        disableGamesWithError(
            state,
            {
                payload,
            }: PayloadAction<{
                errorCode: string;
            }>,
        ) {
            Object.keys(state.slots).forEach((slotId) => {
                const slot = state.slots[slotId];
                slot.errorCode = payload.errorCode;
                slot.hasError = true;
                slot.launchUrl = undefined;
                slot.isLoadingLaunchUrl = false;
            });
        },
        enableGamesWithError(
            state,
            {
                payload,
            }: PayloadAction<{
                errorCode: string;
            }>,
        ) {
            Object.keys(state.slots).forEach((slotId) => {
                const slot = state.slots[slotId];

                if (slot.errorCode === payload.errorCode) {
                    slot.errorCode = undefined;
                    slot.hasError = false;
                }
            });
        },
        setSlotSlug(state, { payload }: PayloadAction<{ slug?: string; slot: number }>) {
            const currentSlotData = state.slots[payload.slot];

            if (!currentSlotData || currentSlotData.slug === payload.slug) {
                return;
            }

            state.slots[payload.slot] = {
                ...currentSlotData,
                isSearchOpen: false,
                isLoadingGameData: false,
                slug: payload.slug,
                internalGameId: '',
                launchUrl: undefined,
            };
        },
    },
    extraReducers(builder) {
        builder.addCase(getGameInformation.pending, (state, { meta: { arg } }) => {
            const slugToSlot = getSlotBy(state.slots, 'slug');
            arg.gameSlugs.forEach((slug) => {
                const slot = slugToSlot[slug];

                if (!slot) {
                    return;
                }

                slot.isLoadingGameData = true;
            });
        });
        builder.addCase(getGameInformation.fulfilled, (state, games) => {
            // TODO: Error states.
            if (!games.payload) {
                return;
            }

            const slugToSlot = getSlotBy(state.slots, 'slug');
            games.payload.getGamesBySlugs?.forEach((game) => {
                const slot = slugToSlot[game.slug];

                if (!slot) {
                    return;
                }

                state.games[game.id] = game;
                slot.internalGameId = game.id;
                slot.isLoadingGameData = false;
            });
        });

        builder.addCase(getLaunchUrls.pending, (state, { meta: { arg } }) => {
            const idToSlot = getSlotBy(state.slots, 'internalGameId');

            arg.games.forEach((game) => {
                const slot = idToSlot[game.internalGameId];

                if (!slot) {
                    // Stale data...
                    return;
                }

                slot.isLoadingLaunchUrl = true;
                slot.errorCode = undefined;
                slot.hasError = false;
                slot.currency = arg.currencyCode;
            });
        });

        builder.addCase(getLaunchUrls.fulfilled, (state, launchUrls) => {
            // TODO: Error states.
            if (!launchUrls.payload) {
                return;
            }

            const urls = launchUrls.payload.games;
            const idToSlot = getSlotBy(state.slots, 'internalGameId');

            urls?.forEach((launchUrlDetails) => {
                const slot = idToSlot[launchUrlDetails.internalGameId];

                if (!slot) {
                    // Stale data...
                    return;
                }

                slot.isLoadingLaunchUrl = false;

                switch (launchUrlDetails.__typename) {
                    case 'GameLaunchError':
                        slot.hasError = true;
                        // Just show the first error to the user.
                        slot.errorCode = launchUrlDetails.errors?.[0].code ?? 'UNKNOWN_ERROR';
                        break;
                    case 'GameLaunchSuccess':
                        slot.hasError = false;
                        slot.currency = launchUrls.payload.currency;
                        slot.errorCode = undefined;
                        slot.launchUrl = launchUrlDetails.url;
                        break;
                }
            });
        });
    },
});

const selectGameplayStore = (state: RootState) => {
    return state[gameplayStore.name];
};

export const selectSlots = createSelector(selectGameplayStore, (state) => {
    return state.slots;
});

export const selectGamesMap = createSelector(selectGameplayStore, (state) => {
    return state.games;
});

export const selectSlotCount = createSelector(selectGameplayStore, (state) => {
    return state.slotCount;
});

export const selectGames = createSelector(selectGamesMap, (games) => {
    return Object.keys(games).map((id) => games[id]);
});

export const selectSlotsWithGames = createSelector(
    [selectSlots, selectGamesMap],
    (slots, games) => {
        return Object.values(slots).map((slot) => {
            return {
                ...slot,
                game: slot.internalGameId ? games[slot.internalGameId] : undefined,
            };
        });
    },
);
